import { getBreakoutById } from '@/domains/breakouts/catalog';
import { currentMove, stageForDay } from '@/domains/breakouts/engine';
import { useMeStore } from '@/store/meStore';

export function BreakoutPage() {
  const enrollment = useMeStore((s) => s.activeEnrollment());
  const goals = useMeStore((s) => s.goals);
  const moveRecords = useMeStore((s) => s.moveRecords);

  if (!enrollment) {
    return (
      <div>
        <h1 className="font-display text-3xl font-black uppercase tracking-hottr-display">
          Breakout
          <span className="text-hottr-pink">.</span>
        </h1>
        <p className="mt-3 text-sm text-white/55">No active Breakout.</p>
      </div>
    );
  }

  const breakout = getBreakoutById(enrollment.breakoutId);
  const goal = goals.find((g) => g.id === enrollment.goalId);
  const current = currentMove(enrollment);
  const records = moveRecords.filter((r) => r.enrollmentId === enrollment.id);

  if (!breakout) return null;

  return (
    <div className="space-y-8">
      <header>
        <p className="text-[10px] font-extrabold uppercase tracking-hottr-stamp text-hottr-pink">
          {breakout.durationDays}-day · {enrollment.status}
        </p>
        <h1 className="mt-1 font-display text-3xl font-black uppercase tracking-hottr-display">
          {breakout.title}
          <span className="text-hottr-pink">.</span>
        </h1>
        <p className="mt-2 text-sm text-white/55">{breakout.tagline}</p>
        {goal && (
          <p className="mt-3 text-[13px] text-white/72">
            Goal: <span className="text-white">{goal.statement}</span>
          </p>
        )}
      </header>

      <section>
        <h2 className="mb-3 text-[10px] font-extrabold uppercase tracking-hottr-stamp text-white/48">
          Stages
        </h2>
        <ol className="space-y-2">
          {breakout.stages.map((stage) => {
            const active =
              enrollment.currentDay >= stage.dayStart &&
              enrollment.currentDay <= stage.dayEnd;
            return (
              <li
                key={stage.id}
                className={[
                  'rounded-sm border px-4 py-3',
                  active
                    ? 'border-hottr-pink/40 bg-hottr-pink/5'
                    : 'border-white/10 bg-white/[0.02]',
                ].join(' ')}
              >
                <p className="text-[12px] font-extrabold uppercase tracking-hottr-label">
                  {stage.title}{' '}
                  <span className="font-semibold text-white/40">
                    · Days {stage.dayStart}–{stage.dayEnd}
                  </span>
                </p>
                <p className="mt-1 text-[12px] text-white/55">{stage.intent}</p>
              </li>
            );
          })}
        </ol>
      </section>

      <section>
        <h2 className="mb-3 text-[10px] font-extrabold uppercase tracking-hottr-stamp text-white/48">
          Progress
        </h2>
        <p className="text-sm text-white/72">
          Day {enrollment.currentDay} of {breakout.durationDays}
          {current?.stage
            ? ` · ${current.stage.title}`
            : stageForDay(breakout, enrollment.currentDay)
              ? ''
              : ''}
        </p>
        <div className="mt-3 h-1 w-full overflow-hidden rounded-sm bg-white/10">
          <div
            className="h-full bg-hottr-pink shadow-neon-soft transition-[width] duration-slow ease-mechanical"
            style={{
              width: `${Math.min(100, ((enrollment.currentDay - 1) / breakout.durationDays) * 100)}%`,
            }}
          />
        </div>
        <p className="mt-2 text-[12px] text-white/40">
          {records.filter((r) => r.outcome === 'complete').length} Moves completed ·{' '}
          {records.filter((r) => r.outcome === 'hard').length} hard days owned ·{' '}
          {records.filter((r) => r.outcome === 'skip').length} skips
        </p>
      </section>
    </div>
  );
}
